/**
 * Plan-gated export rules (docs/spec/08-plan-features.md §1-2). Feature flags come from
 * `entitlement.featureFlags` (GET /v1/me); a missing flag counts as false (Free).
 */
export type FeatureFlags = Record<string, boolean | undefined>;

/** Long-side cap for exports without `hd_export` (Free). */
export const FREE_EXPORT_LONG_SIDE = 1600;

/** Max long side in px for exported images, or null for full photo resolution (`hd_export`). */
export function exportMaxSize(flags: FeatureFlags): number | null {
  return flags.hd_export ? null : FREE_EXPORT_LONG_SIDE;
}

/**
 * Whether exports carry the "Made with Better Lights" watermark. The spec says Free plans only;
 * every paid plan has `hd_export`, so the rule is: an explicit `watermark` flag if the server
 * sends one, else !hd_export.
 */
export function exportWatermark(flags: FeatureFlags): boolean {
  return flags.watermark ?? !flags.hd_export;
}

/** Whether an additional HDR file is exported (Pro+, `hdr_export`; web stays SDR). */
export function exportHdr(flags: FeatureFlags): boolean {
  return flags.hdr_export === true;
}

/**
 * Pixel size of an exported image: scaled down (never up) so the long side ≤ maxSize, each side
 * Math.round()ed and at least 1. Full size when maxSize is null.
 */
export function exportSize(width: number, height: number, maxSize: number | null): { width: number; height: number; scale: number } {
  const long = Math.max(width, height);
  const scale = maxSize == null || long <= maxSize ? 1 : maxSize / long;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)), scale };
}
